import {Injectable, OnDestroy} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {cloneDeep, isEqualDeep} from 'app/core/helpers/object';
import {BehaviorSubject, EMPTY, Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {GraphQLValidationError} from '../error-handler/errors/grahpql/validation-error';
import {InternalError} from '../error-handler/errors/internal-error';
import {LocalAlertsService} from 'app/core/services/alerts/local-alerts.service';
import {AlertStatus} from 'app/core/interfaces/alert.interface';

interface FormServiceOptions {
    checkClientValidity?: boolean;
}

const defaultOptions: FormServiceOptions = {
    checkClientValidity: false,
};

export enum FromSubmitStatus {
    NoAttempts,
    Pending,
    Canceled,
    Success,
    Failed,
}

@Injectable()
export class FormService implements OnDestroy {
    constructor(private alerts: LocalAlertsService) {}

    lastSubmitStatus$ = new BehaviorSubject<FromSubmitStatus>(FromSubmitStatus.NoAttempts);

    public pending$ = new BehaviorSubject(false);
    public readonly errorMessages$ = new BehaviorSubject('');
    public readonly hasErrors$ = new BehaviorSubject(false);
    error: GraphQLValidationError;
    fields: Record<string, string[]> = {};
    options: FormServiceOptions;

    form: FormGroup;

    originalValidators = new Map<string, ValidatorFn | null>();
    finalValidators = new Map<ValidatorFn, string>();

    initialValue: any;

    catchError = (err: InternalError) => {
        let message = err.userMessage || '';
        if (err instanceof GraphQLValidationError && !err.isHandled) {
            this.fields = err.fields;
        }
        err.isHandled = true;
        this.touchControls();
        if (message.length > 0) {
            this.alerts.addAlert(message, AlertStatus.error);
        }
        this.hasErrors$.next(message.length > 0);
    };

    clearControlErrors(control: FormControl): void {
        const name = Object.keys(this.form.controls).find(controlName => this.form.controls[controlName] === control);
        this.fields[name] = null;
    }

    clearErrors() {
        this.markAllFieldsAsValid();
        this.errorMessages$.next('');
        this.hasErrors$.next(false);
        if (this.form) {
            Object.values(this.form.controls).forEach((control: AbstractControl) => {
                control.updateValueAndValidity();
            });
        }
    }

    ngOnDestroy() {
        this.form = null;
        this.originalValidators.clear();
        this.finalValidators.clear();
    }

    validatorFn = (name: string) => (c: FormControl) => (this.fields[name] ? {serverError: this.fields[name]} : null);

    trimValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value || value === '') {
            return null;
        }

        if (value.startsWith(' ') || value.endsWith(' ')) {
            control.setValue(control.value?.trim(), {emitEvent: false, onlySelf: true});
        }

        return null;
    };

    saveInitialValue() {
        if (this.form) {
            this.initialValue = cloneDeep(this.form.value);
        }
    }

    hasChanges(): boolean {
        return this.form ? !isEqualDeep(this.form.value, this.initialValue) : false;
    }

    formHasBeenChanged() {
        return !this.deepEqual(this.form.value, this.initialValue);
    }

    deepEqual(firstObj: any, secondObj: any) {
        if (firstObj === secondObj) {
            return true;
        }

        if (firstObj === null || secondObj === null || typeof firstObj !== 'object' || typeof secondObj !== 'object') {
            return false;
        }

        if (Array.isArray(firstObj)) {
            firstObj = firstObj.slice().sort((a, b) => this.sortValueEqual(a, b));
        }

        if (Array.isArray(secondObj)) {
            secondObj = secondObj.slice().sort((a, b) => this.sortValueEqual(a, b));
        }

        const aKeys = Object.keys(firstObj);
        const bKeys = Object.keys(secondObj);

        if (aKeys.length !== bKeys.length) {
            return false;
        }

        for (let i = 0; i < aKeys.length; i += 1) {
            const key = aKeys[i];
            if (!bKeys.includes(key) || !this.deepEqual(firstObj[key], secondObj[key])) {
                return false;
            }
        }

        return true;
    }

    sortValueEqual(valFirst, valSecond): number {
        if (typeof valFirst !== 'object' || typeof valSecond !== 'object') {
            return valSecond - valFirst;
        }

        return 'id' in valFirst && 'id' in valSecond ? Number(valSecond.id) - Number(valFirst.id) : Number(valSecond) - Number(valFirst);
    }

    init(form: FormGroup, options = defaultOptions, saveInitialValue = true) {
        this.form = form;
        this.options = {...defaultOptions, ...options};
        if (!this.form) {
            return;
        }

        // attach validator for server error message
        Object.keys(form.controls).forEach((name: string) => {
            const control = this.form.get(name);
            if (this.finalValidators.get(control.validator) === name) {
                return;
            }
            this.originalValidators.set(name, control.validator);
            const fieldValidator = this.validatorFn(name);
            const finalValidator = control.validator ? Validators.compose([fieldValidator, control.validator]) : fieldValidator;
            this.finalValidators.set(finalValidator, name);
            control.setValidators(finalValidator);
        });
        if (saveInitialValue) {
            this.saveInitialValue();
        }
    }

    touchControls() {
        if (this.form) {
            Object.values(this.form.controls).forEach((control: AbstractControl) => {
                control.markAsDirty();
                control.markAsTouched();
                control.updateValueAndValidity();
            });
        }
    }

    submit<T>(requestObservable$: Observable<T>): Observable<T> {
        if (this.pending$.getValue()) {
            // prevent double sending form
            return EMPTY;
        }

        if (this.options && this.options.checkClientValidity) {
            // temporary remove server errors to run only client validation
            const fieldErrors = this.fields;
            this.markAllFieldsAsValid();
            // run validation
            this.touchControls();
            const isInvalid = this.form.invalid;
            // restore server errors
            this.fields = fieldErrors;
            this.touchControls();
            if (isInvalid) {
                return EMPTY;
            }
        } else {
            this.touchControls();
        }

        this.pending$.next(true);
        this.lastSubmitStatus$.next(FromSubmitStatus.Pending);
        let canBeCanceled = true;

        // add some hooks
        requestObservable$ = requestObservable$.pipe(
            tap(
                () => {
                    this.pending$.next(false);
                    canBeCanceled = false;
                    this.lastSubmitStatus$.next(FromSubmitStatus.Success);
                    this.touchControls();
                    this.saveInitialValue();
                },
                () => {
                    canBeCanceled = false;
                    this.lastSubmitStatus$.next(FromSubmitStatus.Failed);
                    this.init(this.form, this.options, false);
                    this.pending$.next(false);
                }
            )
        );

        // to add teardown logic if unsubscribe while sending request
        return new Observable(subscriber => {
            const sub = requestObservable$.subscribe(subscriber);
            return () => {
                sub.unsubscribe();
                this.pending$.next(false);
                if (canBeCanceled) {
                    this.lastSubmitStatus$.next(FromSubmitStatus.Canceled);
                }
            };
        });
    }

    getControl(name): AbstractControl {
        return this.form ? this.form.get(name) : undefined;
    }

    markAllFieldsAsValid() {
        this.fields = {};
    }

    markFieldAsValid(name) {
        delete this.fields[name];
        this.getControl(name).updateValueAndValidity();
    }
}
